/**
 * Presentation rules for the Purchase (supplier invoice) list.
 *
 * Kept out of the component so the money and date decisions can be tested,
 * the same way inventoryPackUtils and purchaseOrderUtils are.
 */

export interface PurchaseRow {
  invoiceNo?: string;
  invoiceDate?: string;
  supplier?: string;
  createdAt?: string;
  updatedAt?: string;
  due?: number;
  [key: string]: any;
}

export const formatAmount = (value: number | string | undefined): string => {
  const num = typeof value === "string" ? parseFloat(value) : value ?? 0;
  return Number.isFinite(num) ? (num as number).toFixed(2) : "0.00";
};

/**
 * The supplier's own invoice date is what staff reconcile against. Rows
 * stored before it was mandatory fall back to when they were entered, and
 * say so rather than passing an entry date off as an invoice date.
 */
export const invoiceDateLabel = (
  row: PurchaseRow
): { date: string; fallback: boolean } => {
  if (row?.invoiceDate) return { date: row.invoiceDate, fallback: false };
  const entered = row?.createdAt || row?.updatedAt;
  if (!entered) return { date: "—", fallback: true };
  return { date: String(entered).split("T")[0], fallback: true };
};

export type DueTone = "owed" | "settled" | "overpaid";

/**
 * Due is derived server-side now, so it can legitimately come back
 * negative when a supplier has been overpaid. That is not "paid" and must
 * not read as green.
 */
export const dueTone = (due: number | string | undefined): DueTone => {
  const amount = Number(due) || 0;
  if (amount > 0) return "owed";
  if (amount < 0) return "overpaid";
  return "settled";
};

/** Supplier name or invoice number - staff search by whichever they hold. */
export const matchesPurchaseSearch = (
  row: PurchaseRow,
  search: string
): boolean => {
  const term = (search || "").trim().toLowerCase();
  if (!term) return true;
  return (
    String(row?.supplier ?? "").toLowerCase().includes(term) ||
    String(row?.invoiceNo ?? "").toLowerCase().includes(term)
  );
};

/** One page of rows, matching the 20-per-page the other lists use. */
export const PURCHASE_PAGE_SIZE = 20;

export type DueFilter = "all" | "owed" | "settled" | "overpaid";

export const DUE_FILTERS: { value: DueFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "owed", label: "Due" },
  { value: "settled", label: "Paid" },
  { value: "overpaid", label: "Overpaid" },
];

export const matchesDueFilter = (row: PurchaseRow, filter: DueFilter): boolean =>
  filter === "all" || dueTone(row?.due) === filter;

/**
 * Filters on the date the row actually shows - the supplier's invoice date,
 * or the entry date the older rows fall back to. A row with no date at all
 * can't be placed in a range, so a bounded search leaves it out rather than
 * quietly including it.
 */
export const matchesDateRange = (
  row: PurchaseRow,
  from: string,
  to: string
): boolean => {
  if (!from && !to) return true;
  const { date } = invoiceDateLabel(row);
  if (!date || date === "—") return false;
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
};

export interface PurchaseFilters {
  search?: string;
  due?: DueFilter;
  from?: string;
  to?: string;
}

export const filterPurchases = (
  rows: PurchaseRow[],
  { search = "", due = "all", from = "", to = "" }: PurchaseFilters
): PurchaseRow[] =>
  (rows || []).filter(
    (row) =>
      matchesPurchaseSearch(row, search) &&
      matchesDueFilter(row, due) &&
      matchesDateRange(row, from, to)
  );

/** BottomTab reports the page; the list hands back that slice. */
export const pageSlice = <T,>(
  rows: T[],
  page: number,
  pageSize: number = PURCHASE_PAGE_SIZE
): T[] => {
  const start = (Math.max(1, page) - 1) * pageSize;
  return (rows || []).slice(start, start + pageSize);
};
